// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Suggests a correction for a mistyped email domain.
 *
 * Registration deliberately does not verify the address, and the address doubles as the username
 * and the only password-reset channel — so a typo silently produces an account nobody can ever
 * recover. This catches the common near-misses at the point of entry without blocking anything.
 *
 * Deliberately conservative: it only ever suggests against a known list, and only for domains
 * that are one or two edits away. A false suggestion on a legitimate company domain is more
 * annoying than the typo it would have prevented, so misses are preferred to false positives.
 */

const KNOWN_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.se',
  'hotmail.co.uk',
  'outlook.com',
  'outlook.se',
  'live.com',
  'live.se',
  'icloud.com',
  'me.com',
  'yahoo.com',
  'yahoo.se',
  'telia.com',
  'bredband.net',
  'comhem.se',
  'spray.se',
  'protonmail.com',
  'proton.me',
];

// One edit covers the ordinary slips (gmial, gmai, gmaill). Two only pays off on longer
// domains, where a single transposition plus a dropped letter is still clearly the same word;
// on a short domain two edits reaches genuinely different names.
const MAX_DISTANCE_SHORT = 1;
const MAX_DISTANCE_LONG = 2;
const LONG_DOMAIN_LENGTH = 10;

/**
 * Optimal string alignment distance — Levenshtein plus adjacent transpositions.
 *
 * Transposition has to count as one edit, not two: `gmial` for `gmail` is the single most
 * common way people mistype a domain, and plain Levenshtein scores it the same as two
 * unrelated substitutions, which puts it out of reach of any threshold tight enough to
 * avoid false positives.
 */
const editDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i += 1) {
    rows.push(new Array<number>(b.length + 1).fill(0));
    rows[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) {
    rows[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      rows[i][j] = Math.min(
        rows[i - 1][j] + 1,
        rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + cost);
      }
    }
  }

  return rows[a.length][b.length];
};

/** The label before the first dot — `gmail` in `gmail.com`. */
const secondLevelLabel = (domain: string): string => domain.split('.')[0] ?? domain;

/**
 * Returns the full corrected address (e.g. `you@gmail.com`), or null when the address looks
 * fine, is unparseable, or the domain is not close enough to anything we know.
 */
export const suggestEmailDomain = (email: string): string | null => {
  const trimmed = (email ?? '').trim();
  if (!trimmed) return null;

  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex <= 0 || atIndex === trimmed.length - 1) return null;

  const localPart = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1).toLowerCase();

  // Already a domain we recognise — nothing to suggest.
  if (KNOWN_DOMAINS.includes(domain)) return null;

  const domainLabel = secondLevelLabel(domain);

  let best: { domain: string; distance: number } | null = null;
  for (const candidate of KNOWN_DOMAINS) {
    // Only the TLD differs (outlook.dk vs outlook.se). That is a different mailbox, not a
    // slip of the finger, and guessing at it would be wrong far more often than right.
    if (secondLevelLabel(candidate) === domainLabel) continue;

    const distance = editDistance(domain, candidate);
    const limit = candidate.length >= LONG_DOMAIN_LENGTH ? MAX_DISTANCE_LONG : MAX_DISTANCE_SHORT;
    if (distance > limit) continue;
    if (!best || distance < best.distance) {
      best = { domain: candidate, distance };
    }
  }

  if (!best) return null;
  return `${localPart}@${best.domain}`;
};
